import loadable from "@loadable/component";
import ErrorBoundary from "@Components/ErrorBoundary";
import loadData from "./data";
import FullPageLoader from "@Components/FullPageLoader";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import getBlogHomeData from "./data/getBlogHomeData";
import getBlogHomeRevampData from "./data/getBlogHomeRevampData";
import type { AppState } from "@Store/index";
import { getBlogLanguageFromPath } from "../utils/blogLanguage";
import isBlogRevampEnabled from "../utils/isBlogRevampEnabled";
import { LATEST_TAB_ID } from "../BlogHomeRevamp/settings";

const BlogHome = loadable(() => import("./BlogHome"), {
  fallback: <FullPageLoader />,
});

const BlogHomeRevamp = loadable(() => import("../BlogHomeRevamp/BlogHomeRevamp"), {
  fallback: <FullPageLoader />,
});

export default (props: any): JSX.Element => {
  const dispatch = useDispatch();
  const location = useLocation();
  const readyStatus = useSelector((state: AppState) => state.blogHomeData.readyStatus);
  const hasLegacyData = useSelector(
    (state: AppState) => state.blogHomeData.homeFeaturedBlogs.length > 0,
  );
  const hasRevampData = useSelector(
    (state: AppState) => !!state.blogHomeData.revampTabPosts[LATEST_TAB_ID],
  );

  const lang = getBlogLanguageFromPath(location.pathname);
  const showRevamp = isBlogRevampEnabled(lang);
  const hasData = showRevamp ? hasRevampData : hasLegacyData;

  const requestedVariant = useRef<string | null>(null);
  const clientRecoveryAttempted = useRef(false);
  const variant = showRevamp ? "revamp" : `legacy-${lang ?? "en"}`;

  useEffect(() => {
    if (requestedVariant.current !== variant) {
      clientRecoveryAttempted.current = false;
    }

    const shouldRecoverFromSsrFailure =
      __CLIENT__ && showRevamp && readyStatus === "failure" && !hasData;

    // Load data on client-side navigation if not already loaded or if data is empty
    const shouldLoadFresh =
      requestedVariant.current !== variant &&
      (readyStatus === "invalid" ||
        (readyStatus !== "request" && readyStatus !== "failure" && !hasData));

    if (shouldRecoverFromSsrFailure && !clientRecoveryAttempted.current) {
      clientRecoveryAttempted.current = true;
      dispatch(getBlogHomeRevampData({}));
      return;
    }

    if (shouldLoadFresh) {
      requestedVariant.current = variant;
      dispatch(
        showRevamp
          ? getBlogHomeRevampData({
              staticContext: __CLIENT__ ? undefined : props?.staticContext,
            })
          : getBlogHomeData({
              staticContext: __CLIENT__ ? undefined : props?.staticContext,
              lang,
            }),
      );
    }
  }, [dispatch, readyStatus, hasData, showRevamp, lang, variant, props?.staticContext]);

  return (
    <ErrorBoundary>
      {showRevamp ? <BlogHomeRevamp {...props} /> : <BlogHome {...props} />}
    </ErrorBoundary>
  );
};

export { BlogHome as PreFetchBlogHome };
export { loadData };
